/**
 * AuthCallback
 *
 * Landing page for the OAuth redirect URI (e.g. /auth/callback).
 * Reads the `code` query param, exchanges it for tokens via completeLogin(),
 * then navigates to the originally requested path.
 *
 * Error cases (missing code, failed exchange) redirect back to /login.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';

export default function AuthCallback() {
  const [searchParams]    = useSearchParams();
  const { completeLogin } = useAuth();
  const navigate          = useNavigate();
  const [error, setError] = useState(null);
  // Guard against React StrictMode double-invocation — the first run removes
  // pkce_state from sessionStorage, so the second run would always fail.
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    const code          = searchParams.get('code');
    const returnedState = searchParams.get('state');
    const oauthErr      = searchParams.get('error');
    const errDesc       = searchParams.get('error_description');

    if (oauthErr) {
      setError(errDesc || oauthErr);
      return;
    }
    if (!code) {
      navigate('/login', { replace: true });
      return;
    }

    completeLogin(code, returnedState)
      .then((redirectTo) => navigate(redirectTo, { replace: true }))
      .catch((err) => setError(err.message));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-parchment px-4">
        <div className="w-full max-w-md rounded-2xl border border-neutral-200 bg-paper p-8 shadow-sm text-center">
          <p className="mb-2 text-sm font-medium text-[#003cc5]">Sign-in failed</p>
          <p className="mb-6 text-sm text-neutral-500">{error}</p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="rounded-lg bg-[#003cc5] px-6 py-2.5 font-semibold text-parchment transition hover:bg-[#0036b1]"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-parchment">
      <div className="flex flex-col items-center gap-3">
        <span className="relative flex h-3 w-3">
          <span className="animate-impact-pulse absolute inline-flex h-full w-full rounded-full bg-[#c0392b] opacity-35" />
          <span className="relative inline-flex h-3 w-3 rounded-full bg-[#c0392b]" />
        </span>
        <p className="text-sm text-neutral-500">Completing sign in…</p>
      </div>
    </div>
  );
}
